// /* eslint-disable unused-imports/no-unused-vars */
// /* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useGetMapListings } from '../../hooks/marketPlace/useGetMapListings';
import { GET_MAP_LISTINGS_PAGE } from '../../hooks/marketPlace/marketPlaceQueries';
import { ApiSearchOrderEnum } from '../../util/enum/enum';
import { Typo } from '../../components/Typo';
import Divider from '../../components/shared/Divider';
import Filters from './components/Filters';
import CategoryChipBar from '../../components/shared/CategoryChipBar/CategoryChipBar';
import ListingsLoadError from './components/ListingsLoadError';
import NoListingsFound from './components/NoListingsFound';
import SearchResult from './components/SearchResult';
import { FormattedMessage, useIntl } from 'react-intl';
import { TRANSLATED_CONSTANTS } from '../../static/translatedConstants';
import {
  CLICKED_ZOOM,
  DEFAULT_LAT_LNG,
  DEFAULT_LOCATION_BOUNDS,
  NUMBER_OF_ITEMS_PER_PAGE,
  ROUTES,
  SHOW_MAP_STATE
} from '../../constants/data';
import { AuthUserState } from '../../context/AuthUserProvider';
import { useAppSelector } from '../../lib/redux/hooks';
import { useBreadcrumb } from '../../context/Breadcrumb';
import { Link, useLocation } from 'react-router-dom';
import useDeviceDetect from '../../hooks/useDeviceDetect';
import MapboxLe from './components/MapboxLe';
import MapIcon from '../../components/icons/MapIcon';
import { useDispatch } from 'react-redux';
import {
  addFilterModal,
  addLatLng,
  requestFly,
  setViewRestored
} from '../../lib/redux/reducers/filter.slice';
import useCurrentLocation from '../../hooks/useLocation';
import { isEmpty } from 'lodash';
import PageSeo from '../../components/shared/PageSeo';

// Shareable/restorable map views (plan F5). Priority: ?bbox in the URL, then
// the saved last view, else null (normal geolocation flow). Anything that
// doesn't parse to finite numbers is ignored silently.
const readRestoredView = (): { bounds: any; zoom: number; center: [number, number] } | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('bbox');
    if (raw) {
      const parts = raw.split(',').map(Number);
      if (parts.length === 4 && parts.every(Number.isFinite)) {
        const [minLng, minLat, maxLng, maxLat] = parts;
        const zoom = Number(params.get('z'));
        return {
          bounds: { minLat, maxLat, minLng, maxLng },
          zoom: Number.isFinite(zoom) ? zoom : 4,
          center: [(minLat + maxLat) / 2, (minLng + maxLng) / 2]
        };
      }
    }
    const stored = localStorage.getItem('lastMapView');
    if (stored) {
      const view = JSON.parse(stored);
      const b = view?.bounds;
      if (
        b &&
        [b.minLat, b.maxLat, b.minLng, b.maxLng].every(Number.isFinite) &&
        Number.isFinite(view?.zoom)
      ) {
        return {
          bounds: b,
          zoom: view.zoom,
          center: [(b.minLat + b.maxLat) / 2, (b.minLng + b.maxLng) / 2]
        };
      }
    }
  } catch {
    // Corrupt URL/storage - fall through to the normal flow.
  }
  return null;
};

const MarketPlace = () => {
  const { authUser } = AuthUserState();
  const intl = useIntl();
  const { addBreadcrumb, breadcrumbs } = useBreadcrumb();
  const location = useLocation();
  const filterFromModal = useAppSelector((state) => state.filter.filterModal);
  const autoWidened = useAppSelector((state) => state.filter.autoWidened);
  const movedAway = useAppSelector((state) => state.filter.movedAway);
  const [loadingMore, setLoadingMore] = useState(false);
  const { isDesktop, isMobile } = useDeviceDetect();
  const dispatch = useDispatch();
  // F2: below 1024px the page is either the list OR a full-screen map,
  // switched by the floating pill (Airbnb pattern). MAP first (Luke's call,
  // 2026-07-07): DogeList's inventory is sparse-and-global, so a local list
  // often leads with far-away items while the world map with clusters tells
  // the real story - and the cream map is the brand moment. The user's last
  // choice is remembered, so list-preferrers keep the list.
  // A link labelled "Map" (menu, header, footer) asks for the map outright.
  const asksForMap = (state: unknown) =>
    !!state && (state as { view?: unknown }).view === SHOW_MAP_STATE.view;
  const [mobileView, setMobileView] = useState<'list' | 'map'>(() => {
    if (asksForMap(location.state)) return 'map';
    try {
      return localStorage.getItem('mapMobileView') === 'list' ? 'list' : 'map';
    } catch {
      return 'map';
    }
  });
  // ...also when this page is already open: tapping Map in the phone menu
  // while the list was showing used to do nothing (Luke, 2026-10-08). Asking
  // for the map counts as choosing it, like the pill, and the request is
  // dropped from the history entry so a reload keeps whatever comes next.
  useEffect(() => {
    if (!asksForMap(location.state)) return;
    setMobileView('map');
    try {
      localStorage.setItem('mapMobileView', 'map');
    } catch {
      // Private mode - preference just won't persist.
    }
    window.scrollTo(0, 0);
    try {
      const entry = window.history.state;
      if (entry && entry.usr) window.history.replaceState({ ...entry, usr: null }, '');
    } catch {
      // The entry keeps its state; harmless.
    }
  }, [location.key]);
  const toggleMobileView = () => {
    window.scrollTo(0, 0);
    setMobileView((view) => {
      const next = view === 'list' ? 'map' : 'list';
      try {
        localStorage.setItem('mapMobileView', next);
      } catch {
        // Private mode - preference just won't persist.
      }
      return next;
    });
  };

  // Searchable URLs: /market-place?q=term seeds the keyword search (links are
  // shareable and indexable), and the URL mirrors the active search via
  // replaceState so the map never sees a router navigation.
  const initialSearchQ = useRef(new URLSearchParams(window.location.search).get('q') || '');
  const seededSearchQ = useRef(false);

  useEffect(() => {
    if (!seededSearchQ.current && initialSearchQ.current) {
      seededSearchQ.current = true;
      dispatch(
        addFilterModal({
          filterModal: { ...filterFromModal, searchText: initialSearchQ.current }
        })
      );
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const current = params.get('q') || '';
    const next = filterFromModal.searchText || '';
    if (current === next) return;
    if (next) {
      params.set('q', next);
    } else {
      params.delete('q');
    }
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  }, [filterFromModal.searchText]);

  const { selectedLocation, isCurrentLocatinLoading } = useCurrentLocation();
  const locationData = useMemo(() => localStorage.getItem('locationData'), []);

  const currentLocation = useMemo(() => {
    return locationData ? JSON.parse(locationData ?? '') : null;
  }, [locationData]);

  // F5: restore a shared/last view once, before the geolocation seeding.
  const restoredViewRef = useRef(readRestoredView());
  const restoreAppliedRef = useRef(false);
  useLayoutEffect(() => {
    const view = restoredViewRef.current;
    if (!view || restoreAppliedRef.current) {
      return;
    }
    restoreAppliedRef.current = true;
    dispatch(setViewRestored(true));
    dispatch(addLatLng({ latLng: { lat: view.center[0], lng: view.center[1], zoom: view.zoom } }));
    dispatch(
      addFilterModal({
        filterModal: {
          ...filterFromModal,
          locationBounds: view.bounds,
          searchText: initialSearchQ.current
        }
      })
    );
    dispatch(requestFly());
    // eslint-disable-next-line
  }, []);

  useLayoutEffect(() => {
    // F5: a restored view is deliberate - geolocation seeding stands down.
    if (restoredViewRef.current) {
      return;
    }
    const defaultGlobalLatLng = {
      lat: DEFAULT_LAT_LNG.lat, // Set your default latitude here
      lng: DEFAULT_LAT_LNG.lng, // Set your default longitude here
      zoom: DEFAULT_LAT_LNG.zoom // Set your default zoom level here
    };
    if (isCurrentLocatinLoading === -1) {
      let lat = defaultGlobalLatLng.lat;
      let lng = defaultGlobalLatLng.lng;
      let zoom = defaultGlobalLatLng.zoom;
      let locData = null;
      let address = '';
      let locationBounds = DEFAULT_LOCATION_BOUNDS;

      if (currentLocation && !isEmpty(currentLocation)) {
        lat = currentLocation?.location?.latitude;
        lng = currentLocation?.location?.longitude;
        zoom = CLICKED_ZOOM;
        locData = currentLocation?.location;
        locationBounds = currentLocation?.locationBound;
        address = currentLocation?.location?.address;
      }
      if (
        JSON.stringify(filterFromModal.locationBounds) ===
        JSON.stringify(selectedLocation.locationBound)
      ) {
        dispatch(
          addLatLng({
            latLng: {
              lat,
              lng,
              zoom
            }
          })
        );

        dispatch(
          addFilterModal({
            filterModal: {
              locationData: locData,
              locationBounds,
              address,
              isFirstValue: true,
              // Category discovery fix: seeding is about LOCATION - it must
              // not wipe the visitor's active refinements. This effect re-runs
              // on every marketplace mount (e.g. returning from a listing), and
              // the old hardcoded moreFilter/categories reset silently cleared
              // the chip-bar selection on the way back to the map.
              searchText: filterFromModal?.searchText || initialSearchQ.current,
              moreFilter: filterFromModal?.moreFilter ?? null,
              categories: filterFromModal?.categories ?? []
            }
          })
        );
      }
    }
  }, [isCurrentLocatinLoading]);

  useEffect(() => {
    addBreadcrumb({
      link: location.pathname,
      name: intl.formatMessage({
        id: TRANSLATED_CONSTANTS.MAP_VIEW.id,
        defaultMessage: TRANSLATED_CONSTANTS.MAP_VIEW.defaultMessage
      }),
      index: breadcrumbs.length
    });
  }, []);

  // F5: mirror the current view into ?bbox=&z= (replaceState, same pattern as
  // ?q=) and remember it as the last view for the next visit. 5 decimals ~ 1m.
  // (The mirror effect itself lives below mapDataLength's declaration.)
  const latLngState = useAppSelector((state) => state.filter.latLng);

  const {
    loading,
    error,
    fetchMore,
    refetch,
    variables,
    listings = [],
    storefronts = [],
    wantedListingsCount = 0,
    personalListingsCount = 0,
    storefrontItemsCount = 0,
    eventsCount = 0,
    storefrontsCount = 0
  } = useGetMapListings(
    filterFromModal?.moreFilter,
    filterFromModal.categories,
    filterFromModal.searchText,
    filterFromModal.locationBounds,
    0,
    0,
    ApiSearchOrderEnum.PUBLISHED_DESC,
    authUser?.id || null
  );

  const mapDataLength =
    wantedListingsCount +
    personalListingsCount +
    storefrontsCount +
    storefrontItemsCount +
    eventsCount;

  // F5: mirror the current view into ?bbox=&z= (replaceState, same pattern
  // as ?q=). Lives BELOW mapDataLength (the gate caught a used-before-
  // declaration when it sat higher up).
  useEffect(() => {
    const b = filterFromModal?.locationBounds;
    if (!b || ![b.minLat, b.maxLat, b.minLng, b.maxLng].every(Number.isFinite)) {
      return;
    }
    const zoom = Number.isFinite(latLngState?.zoom) ? latLngState.zoom : 4;
    const bbox = [b.minLng, b.minLat, b.maxLng, b.maxLat]
      .map((n: number) => Number(n).toFixed(5))
      .join(',');
    const params = new URLSearchParams(window.location.search);
    params.set('bbox', bbox);
    params.set('z', String(zoom));
    window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
    // F7 fix: only views that actually SHOWED something become the next
    // visit's homecoming view - a dead-empty view (found in QA: a leftover
    // Null-Island pan got restored) shouldn't greet you tomorrow. The URL
    // above still mirrors everything for shareability.
    if (mapDataLength > 0) {
      try {
        localStorage.setItem('lastMapView', JSON.stringify({ bounds: b, zoom }));
      } catch {
        // Private mode - the URL still carries the view.
      }
    }
  }, [filterFromModal?.locationBounds, latLngState?.zoom, mapDataLength]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      // Page-only document: same field args (so pages merge into the same
      // cached lists) but skips the five count aggregates per click.
      // A fetchMore with its OWN document does not inherit the list query's
      // variables (Apollo only merges them when the document is the same), so
      // the filters, sort and user must be passed here. Without them the
      // server rejected every Load more (400: $skip not provided), and even a
      // passing request would have paged the whole world into a different
      // cache key. Same filters + sort = same cache key = the page appends.
      await fetchMore({
        query: GET_MAP_LISTINGS_PAGE,
        variables: {
          filters: variables?.filters,
          storefrontFilters: variables?.storefrontFilters,
          userId: variables?.userId,
          order: variables?.order,
          skip: variables?.skip,
          listingOffset: listings.length,
          storefrontOffset: storefronts.length,
          limit: NUMBER_OF_ITEMS_PER_PAGE
        }
      });
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <>
      <PageSeo
        title={intl.formatMessage({
          id: 'page.marketplace.seo_title',
          defaultMessage: 'Dogecoin Marketplace'
        })}
        h1={intl.formatMessage({
          id: 'page.marketplace.seo_h1',
          defaultMessage: 'Dogecoin Marketplace'
        })}
      />
      <div className=" grid grid-cols-1 relative lg:grid-cols-2 lg:divide-x lg:divide-gray-200 w-full lg:mx-auto overflow-x-clip">
        {isCurrentLocatinLoading === -1 && (
          <>
            {/* F2: full-screen map view for <1024px. The list column below is
                hidden while this is showing; the floating pill toggles back. */}
            {!isDesktop && mobileView === 'map' && (
              <div className="w-full lg:hidden">
                {/* F7 fix: subtract any active banner (--banner-h is set by
                    VerifyEmailBanner while visible) so the map - and the
                    pin-tap card docked to its bottom - never extend below
                    the fold. Static 72px alone clipped the card by the
                    banner's height (measured live by the dashboard session). */}
                <MapboxLe
                  width="100%"
                  height="calc(100dvh - 72px - var(--banner-h, 0px))"
                  isMobile={isMobile}
                  sheetMode={true}
                />
              </div>
            )}
            <div
              className={`pt-12 pb-2 lg:pt-8 lg:pb-2 ${
                !isDesktop && mobileView === 'map' ? 'hidden lg:block' : ''
              }`}>
              <div className="px-3 md:px-12 lg:pe-8 lg:ps-[60px]">
                <div className="mb-6">
                  {/* aria-live (F6): screen readers hear result-count changes
                      after pans and filter toggles. */}
                  <div
                    aria-live="polite"
                    aria-atomic="true"
                    className="font-poppins text-2xl mb-1 leading-8 md:text-3xl md:leading-[44px] text-gray-900 font-normal">
                    {mapDataLength}{' '}
                    <FormattedMessage
                      id={TRANSLATED_CONSTANTS.MARKET_PLACE_TITLE.id}
                      defaultMessage={TRANSLATED_CONSTANTS.MARKET_PLACE_TITLE.defaultMessage}
                    />{' '}
                    <span className="font-semibold">
                      {!movedAway && filterFromModal.address ? (
                        filterFromModal.address
                      ) : (
                        <FormattedMessage
                          id={TRANSLATED_CONSTANTS.IN_THIS_AREA.id}
                          defaultMessage={TRANSLATED_CONSTANTS.IN_THIS_AREA.defaultMessage}
                        />
                      )}
                    </span>
                  </div>
                  <Typo.textMd fontWeight="font-normal" className="text-gray-500">
                    <FormattedMessage
                      id={TRANSLATED_CONSTANTS.MARKET_PLACE_SUBTITLE.id}
                      defaultMessage={TRANSLATED_CONSTANTS.MARKET_PLACE_SUBTITLE.defaultMessage}
                    />
                  </Typo.textMd>
                  {autoWidened && (
                    <Typo.textSm fontWeight="font-normal" className="text-gray-500 mt-1">
                      <FormattedMessage
                        id={TRANSLATED_CONSTANTS.MAP_AUTO_WIDENED_NOTE.id}
                        defaultMessage={TRANSLATED_CONSTANTS.MAP_AUTO_WIDENED_NOTE.defaultMessage}
                      />{' '}
                      <Link
                        to={ROUTES.CREATE_LISTING_ROUTE}
                        className="font-medium text-yellow-700 underline hover:text-yellow-800">
                        <FormattedMessage
                          id={TRANSLATED_CONSTANTS.BE_FIRST_TO_LIST.id}
                          defaultMessage={TRANSLATED_CONSTANTS.BE_FIRST_TO_LIST.defaultMessage}
                        />
                      </Link>
                    </Typo.textSm>
                  )}
                </div>
                <Divider />

                <Filters />
                {/* Category discovery P0: the persistent category chip bar -
                    one tap to filter by what a thing IS (was ~7 taps deep in
                    the sheet). Sticky under the 72px header as the list
                    scrolls; the map view renders its own overlay placement
                    inside MapboxLe. */}
                <CategoryChipBar placement="list" />
                {/* F2: below 1024px the old mid-page map bands are gone - the
                    map is a full-screen view behind the floating toggle pill
                    (rendered further down), Airbnb-style. */}
                <div className="mt-8">
                  {error && !loading ? (
                    // Failed fetch must not impersonate an empty marketplace
                    // (map plan B4). Retry refetches the list query AND
                    // re-dispatches the filter modal so the map-pins hook
                    // (plain fetch, keyed on argument identity) refires too.
                    <ListingsLoadError
                      onRetry={() => {
                        refetch();
                        dispatch(addFilterModal({ filterModal: { ...filterFromModal } }));
                      }}
                    />
                  ) : mapDataLength < 1 && !loading ? (
                    <NoListingsFound />
                  ) : (
                    <SearchResult
                      loading={loading}
                      storefronts={storefronts}
                      listings={listings}
                      personalListingsCount={personalListingsCount}
                      storefrontsCount={storefrontsCount}
                      wantedListingsCount={wantedListingsCount}
                      storefrontItemsCount={storefrontItemsCount}
                      eventsCount={eventsCount}
                      onLoadMore={loadMore}
                      loadingMore={loadingMore}
                      refetch={refetch}
                    />
                  )}
                </div>
              </div>
            </div>
            {isDesktop && (
              <div className="hidden lg:block relative p-3">
                {/* Sticky, not fixed (map plan F1): the old fixed wrapper kept
                    its in-flow offset, so with height calc(100vh-96px) the
                    map's bottom strip (attribution, my-location, cap chip)
                    hung below the viewport. Sticky pins the top at the header
                    and the dvh height lands the bottom exactly on the fold. */}
                {/* F7 fix: banner-aware height here too - with the banner up
                    the sticky column starts lower, so the bottom strip
                    (attribution, my-location) hid below the fold. Accepted
                    trade: after scrolling the banner away a same-height gap
                    sits under the map until the banner is dismissed/verified. */}
                <div className="sticky top-[96px] h-[calc(100dvh-96px-var(--banner-h,0px))]">
                  <MapboxLe width="100%" height="100%" />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {/* F2: the floating map/list pill, <1024px only. Safe-area padded so it
          clears iPhone home indicators. z-[5] (F7 round 3): above page/list
          content and the map's z-0 stacking context, under the pin sheet
          (z-[6]) and the portaled slide/modal layers (z-10/z-30) - at the
          old z-40 it poked through the Message Seller drawer. */}
      {isCurrentLocatinLoading === -1 && !isDesktop && (
        <button
          type="button"
          onClick={toggleMobileView}
          className="fixed bottom-[calc(16px+env(safe-area-inset-bottom))] left-1/2 z-[5] flex -translate-x-1/2 items-center gap-2 rounded-full bg-yellow-400 px-5 py-3 text-sm font-semibold text-gray-900 shadow-lg hover:bg-yellow-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 lg:hidden">
          {mobileView === 'list' ? (
            <>
              <MapIcon className="h-4 w-4" />
              <FormattedMessage
                id={TRANSLATED_CONSTANTS.MAP_VIEW.id}
                defaultMessage={TRANSLATED_CONSTANTS.MAP_VIEW.defaultMessage}
              />
            </>
          ) : (
            <FormattedMessage
              id={TRANSLATED_CONSTANTS.SHOW_N_LISTINGS.id}
              defaultMessage={TRANSLATED_CONSTANTS.SHOW_N_LISTINGS.defaultMessage}
              values={{ count: mapDataLength }}
            />
          )}
        </button>
      )}
    </>
  );
};

export default MarketPlace;
